/**
 * Erreur normalisée de l'API.
 *
 * Toutes les erreurs réseau passent par ici avant d'atteindre un composant, pour deux raisons.
 *
 * D'abord, un message technique brut — un `SQLSTATE`, une trace — ne doit jamais s'afficher : il
 * n'aide pas l'assuré et renseigne un attaquant. Ensuite, distinguer les cas demande le code
 * HTTP, et le lire depuis `error.response.status` à chaque appel finit toujours par être oublié
 * quelque part.
 */

/** Détail d'erreur v2 : un libellé par champ pour une 422, des clés libres sinon. */
export type ApiErrorDetails = Record<string, unknown>;

export class ApiError extends Error {
    constructor(
        /** Code métier renvoyé par le Pass (`invalid_credentials`, `no_password_set`…) ou code interne. */
        readonly code: string,
        message: string,
        /** Statut HTTP. `0` quand la réponse n'est jamais arrivée. */
        readonly httpStatus: number,
        /**
         * Identifiant de corrélation généré par le Pass (`error.id`, aussi en en-tête
         * `X-Error-Id`). C'est lui qui permet de retrouver l'incident dans ses journaux : à
         * afficher quand on demande à l'assuré de rappeler, à logger toujours.
         */
        readonly id?: string,
        /** `error.details` de l'enveloppe v2. Cf. les accesseurs ci-dessous, qui le lisent. */
        readonly details?: ApiErrorDetails,
    ) {
        super(message);
        this.name = 'ApiError';
    }

    /** La session est finie — le seul cas qui justifie de renvoyer à la connexion. */
    get isUnauthenticated(): boolean {
        return this.httpStatus === 401;
    }

    /**
     * Le service est en défaut, pas la session.
     *
     * Un 429 ou un 503 renvoyés à l'écran de connexion, c'est une déconnexion apparente alors que
     * le cookie est valide. La distinction n'est pas théorique : elle s'est vue en production sur
     * l'Espace Conseiller quand le compteur de requêtes, partagé par adresse IP, rendait des 429.
     */
    get isServiceUnavailable(): boolean {
        return this.httpStatus === 0 || this.httpStatus === 429 || this.httpStatus >= 500;
    }

    /** Un microservice précis est tombé (CMS, GED, Deals…), le reste du Pass répond. */
    get isMicroserviceUnavailable(): boolean {
        return this.code === 'MICROSERVICE_UNAVAILABLE';
    }

    /** Microservice en défaut, tel que nommé par le Pass dans `details.service`. */
    get service(): string | undefined {
        return typeof this.details?.['service'] === 'string' ? this.details['service'] : undefined;
    }

    /** Délai de réessai conseillé, en secondes, quand le Pass le précise. */
    get retryAfter(): number | undefined {
        return typeof this.details?.['retry_after'] === 'number'
            ? this.details['retry_after']
            : undefined;
    }

    /**
     * Écriture partie sans réponse : le Pass ignore si elle a été appliquée (504).
     *
     * À ne **jamais** confondre avec une panne, et c'est pour ça que ce cas a son propre
     * accesseur. Le message d'une indisponibilité invite à réessayer ; or une seconde tentative
     * repartirait avec une nouvelle clé d'idempotence et appliquerait l'acte de gestion deux fois
     * — un hivernage facturé en double, une résiliation posée deux fois. Le message vient du back,
     * qui dit d'aller vérifier le contrat.
     */
    get isOutcomeUnknown(): boolean {
        return this.code === 'OPERATION_OUTCOME_UNKNOWN';
    }

    /** Clé d'idempotence de l'opération — à relayer au support pour rapprocher les états. */
    get operationUuid(): string | undefined {
        return typeof this.details?.['operation_uuid'] === 'string'
            ? this.details['operation_uuid']
            : undefined;
    }

    /**
     * Ce qui est réellement invalide, pour une 422.
     *
     * L'enveloppe v2 met « Les données fournies sont invalides » en `message` et le détail utile
     * en `details` — un libellé par champ, déjà rédigé en français par la Form Request du Pass.
     * Affiché seul, le message générique laisse l'assuré relire son formulaire sans savoir quoi
     * corriger.
     *
     * `undefined` plutôt qu'une chaîne vide quand il n'y a rien à dire : l'appelant retombe alors
     * sur `message`, au lieu d'afficher un blanc.
     */
    get validationMessage(): string | undefined {
        if (this.httpStatus !== 422 || this.details === undefined) {
            return undefined;
        }

        const messages = Object.values(this.details)
            .flat()
            .filter((message): message is string => typeof message === 'string' && message !== '');

        return messages.length === 0 ? undefined : [...new Set(messages)].join(' · ');
    }

    /**
     * Champ par champ, pour recâbler les erreurs du Pass dans un formulaire RHF.
     *
     * `validationMessage` sert le toast ; ceci sert `setError(champ, …)`. Les deux existent parce
     * qu'une 422 se lit à deux endroits : au-dessus du formulaire et sous le champ fautif.
     */
    get fieldErrors(): Record<string, string> {
        if (this.httpStatus !== 422 || this.details === undefined) {
            return {};
        }

        const entries = Object.entries(this.details).flatMap(([field, value]) => {
            // `as unknown[]` : `Array.isArray` sur un `unknown` le réduit à `any[]`, et
            // `value[0]` deviendrait un `any` qui traverse le typage sans rien dire.
            const first: unknown = Array.isArray(value) ? (value as unknown[])[0] : value;

            return typeof first === 'string' && first !== '' ? [[field, first] as const] : [];
        });

        return Object.fromEntries(entries);
    }
}
